import React from "react";

import {
  UserOrderResponse,
  OrderItemResponse,
  InvoiceResponse,
  InvoiceCommercialResponse,
  PaymentScheduleResponse,
} from "@/api";

import OrderDetailsSection from "../components/order/OrderDetailsSection";
import OrderItemsSection from "../components/orderitem/OrderItemsSection";
import InvoiceDetailsSection from "../components/invoice/InvoiceDetailsSection";
import InvoiceCommercialsSection from "../components/commercial/InvoiceCommercialsSection";
import PaymentScheduleSection from "../components/payment_schedule/PaymentScheduleSection";
import { AdminOrderUpdateService } from "../service/adminOrderUpdateServices";
import { useNavigate } from "react-router-dom";

export interface AdminOrderFormProps {
  orderId: number;

  order: UserOrderResponse;

  orderItems: OrderItemResponse[];

  invoice: InvoiceResponse | null;

  invoiceCommercials: InvoiceCommercialResponse | null;

  paymentSchedules: PaymentScheduleResponse[];

  /*
   * Section responses.
   *
   * Each section saves through its own service and
   * returns the updated DTO. The parent only replaces
   * that section in the aggregate state.
   */

  onOrderChange?: (
    order: UserOrderResponse
  ) => void;

  onOrderItemsSaved?: () => void;
  
  onInvoiceChange?: (
    invoice: InvoiceResponse
  ) => void;

  onInvoiceCommercialsSaved?: () => void;

  onPaymentSchedulesChange?: (
    schedules: PaymentScheduleResponse[]
  ) => void;

  approving?: boolean;
}

export default function AdminOrderForm({
  orderId,
  order,
  orderItems,
  invoice,
  invoiceCommercials,
  paymentSchedules,

  onOrderChange,
  onOrderItemsSaved,
  onInvoiceChange,
  onInvoiceCommercialsSaved,
  onPaymentSchedulesChange,

  approving = false,
}: AdminOrderFormProps) {

  const navigate = useNavigate();

  const handleInvoiceApprove = async () => {
    try {
      if(!invoice) return;
        await AdminOrderUpdateService.approveInvoice(
          invoice?.id,
        );
          navigate("/admin");
      } catch (err) {
        console.log("Error in Approving Invoice"+err);
      } 
    }

  return (
    <div className="space-y-6">

      {/* ==================================================
          HEADER
      ================================================== */}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            Order #{order.id}
          </h1>

          <p className="mt-1 text-sm text-gray-500">
            Review the order, invoice, commercials and
            payment schedule before approval.
          </p>
        </div>

        <button
          type="button"
          onClick={handleInvoiceApprove}
          disabled={
            approving ||
            !invoice ||
            invoice.invoice_status === "APPROVED"
          }
          className="rounded-md bg-black px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {approving
            ? "Approving..."
            : invoice?.invoice_status === "APPROVED"
              ? "Approved"
              : "Approve Invoice"}
        </button>
      </div>

      {/* ==================================================
          ORDER DETAILS
      ================================================== */}

      <OrderDetailsSection
              orderId={order.id}
              value={order}
              onChange={(updatedOrder) => {
                  onOrderChange?.(updatedOrder);
              } }       
              />

      {/* ==================================================
          ORDER ITEMS
      ================================================== */}

        <OrderItemsSection
          orderId={orderId}
          items={orderItems}
          onChange={() => {
            onOrderItemsSaved?.();
          }}
        />

      {/* ==================================================
          INVOICE
      ================================================== */}

      {invoice && (
        <>
          <InvoiceDetailsSection
            orderId={orderId}
            value={invoice}
            onChange={(updatedInvoice) => {
              onInvoiceChange?.(updatedInvoice);
            }}
          />

          {/* -----------------------------------------------
              COMMERCIALS
          ------------------------------------------------ */}

          {invoiceCommercials && (
            <InvoiceCommercialsSection
              value={invoiceCommercials}
              onSaved={onInvoiceCommercialsSaved}
            />
          )}
          {/* -----------------------------------------------
              PAYMENT SCHEDULE
          ------------------------------------------------ */}

          <PaymentScheduleSection
            finalAmount={
              Number(invoiceCommercials?.final_amount) ?? 0
            }
            schedules={paymentSchedules}
            onChange={(updatedSchedules) => {
              onPaymentSchedulesChange?.(
                updatedSchedules
              );
            }}
          />
        </>
      )}

      {/* ==================================================
          APPROVAL FOOTER
      ================================================== */}

      <div className="flex justify-end border-t pt-6">
        <button
          type="button"
          onClick={handleInvoiceApprove}
          disabled={
            approving ||
            !invoice ||
            invoice.invoice_status === "APPROVED"
          }
          className="rounded-md bg-black px-5 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {approving
            ? "Approving..."
            : invoice?.invoice_status === "APPROVED"
              ? "Invoice Approved"
              : "Approve Invoice"}
        </button>
      </div>
    </div>
  );
}